import React, { useEffect, useState } from 'react';
import { getPreviewData } from './BOH/helpers/BOHPreviewData';
import { getLayoutPreviewData } from './CustomLayout/layoutHelpers';
import UPCode from './components/UPcode';
import validate from './spotlightValidator';
import './SpotlightPlayer.scss';
import BOHComponent from './BOH/BOHComponent';
import CustomLayoutComponent from './CustomLayout/CustomLayoutComponent';
import Background from './components/Background';
import Foreground from './components/Foreground';
import TextOverlay from './components/TextOverlay';
import { loadCustomFonts } from './helpers/custom_fonts';
import { isDefined, showQRShortURL } from './helpers/utils';
import { DocumentPaged } from './components/DocumentPaged';
const brandkitDefault = {
  title_color: '#212529',
  title_font_family: "'Montserrat', sans-serif",
  body_color: '#212529',
  body_font_family: "'Anton', sans-serif",
  font_accent_color: '#CED4DA',
  accent_color: '#CED4DA',
  background_color: '#FFFFFF'
};
const bohDefaultBackgrounds = {
  boh_type_charity: 'charity',
  boh_type_cleaning: 'cleaning_policy',
  boh_type_birthday: 'birthday',
  boh_type_birthday_single_employee: 'birthday',
  boh_type_multi_birthday: 'birthday',
  boh_type_anniversary: 'anniversary',
  boh_type_anniversary_single_employee: 'anniversary',
  boh_type_multi_anniversary: 'anniversary',
  boh_type_huddle_notes: 'shift_notes',
  boh_type_bio: 'employee_bio',
  boh_type_payday: 'payday'
};
const BOHSpotlightWithoutOwnTemplate = ['boh_type_charity', 'boh_type_cleaning'];
const getTextConfiguration = (text, brandkit) => {
  return {
    body_color: text.body_color || brandkit.font_body_color || brandkitDefault.body_color,
    body_font_family: text.body_font_family || brandkit.font_body_family || brandkitDefault.body_font_family,
    body_font_size: text.body_font_size,
    accent_color: text.accent_color || brandkit.font_accent_color || brandkitDefault.font_accent_color,
    title_color: text.title_color || brandkit.font_title_color || brandkitDefault.title_color,
    title_font_family: text.title_font_family || brandkit.font_title_family || brandkitDefault.title_font_family,
    title_font_size: text.title_font_size
  };
};
const transformTextData = (text, brandkit) => {
  return {
    ...text,
    ...getTextConfiguration(text, brandkit)
  };
};
const transformMetadata = (metadata, brandkit) => {
  return {
    ...metadata,
    logo: metadata.logo || metadata.logo === null ? metadata.logo : brandkit.logo,
    accent_color: metadata.accent_color || brandkit.accent_color || brandkitDefault.accent_color,
    background_color: metadata.background_color || brandkit.background_color || brandkitDefault.background_color,
    hide_poweredbyshift: isDefined(metadata.hide_poweredbyshift) ? metadata.hide_poweredbyshift : brandkit.hide_poweredbyshift
  };
};
const SpotlightPlayer = ({
  volume = 0.8,
  spotlight,
  showPreview,
  loop,
  style,
  playing,
  onProgress,
  muted,
  onReady,
  onPlay,
  onPause,
  onEnded,
  onError,
  onDuration,
  onBackgroundClick,
  clickable,
  deviceId,
  organizationId,
  useCors,
  reactPlayerRef,
  reactPlayerConfig,
  origin,
  logger,
  brandkit = {}
}) => {
  var _spotlight$metadata, _spotlight$text, _brandkit$bohBackgrou, _background, _background$metadata;
  const {
    duration,
    properties,
    TrackingCode
  } = spotlight;
  const [fontReady, setFontReady] = useState(false);
  const [backgroundReady, setBackgroundReady] = useState(false);
  const [documentPagesReady, setDocumentPagesReady] = useState(false);
  const metadata = transformMetadata((_spotlight$metadata = spotlight.metadata) !== null && _spotlight$metadata !== void 0 ? _spotlight$metadata : {}, brandkit);
  const vertical = properties.includes('clockwise_rotated');
  const rotated = properties.includes('rotated'); // If the spotlight is rotated the background asset will be rotated because of v1 spotlights
  const logo = metadata.logo;
  const accent_color = metadata.accent_color;
  const {
    custom_fonts = []
  } = metadata;
  const log = logger !== null && logger !== void 0 ? logger : console;
  const showShortURL = showQRShortURL(metadata.campaign_type);
  let customLayoutData = metadata.custom_layout;
  useEffect(() => {
    if (fontReady && (backgroundReady || documentPagesReady) && onReady) {
      onReady();
    }
  }, [fontReady, backgroundReady, documentPagesReady, onReady]);
  useEffect(() => {
    async function fetchCustomFonts() {
      await loadCustomFonts(custom_fonts, log);
      setFontReady(true);
    }
    fetchCustomFonts();
  }, [custom_fonts, log]);
  const valid_spotlight = validate(spotlight);
  const isDocument = properties.find(p => p === 'document_spotlight');
  const needsBOHTemplate = properties.find(p => p === 'boh_spotlight') && !properties.some(p => BOHSpotlightWithoutOwnTemplate.includes(p));
  const boh_type = properties.find(p => p.startsWith('boh_type_'));
  const customLayoutType = properties.find(p => p.startsWith('layout_type_'));
  const text = transformTextData((_spotlight$text = spotlight.text) !== null && _spotlight$text !== void 0 ? _spotlight$text : {}, brandkit);
  let {
    boh_data,
    background
  } = spotlight;
  if ((!boh_data || valid_spotlight !== true) && showPreview) {
    if (needsBOHTemplate) boh_data = getPreviewData(boh_type, showPreview === 'empty', metadata);
    if (customLayoutType) customLayoutData = getLayoutPreviewData(customLayoutData, text);
  }
  if (!background) background = (_brandkit$bohBackgrou = brandkit.bohBackgrounds) === null || _brandkit$bohBackgrou === void 0 ? void 0 : _brandkit$bohBackgrou[bohDefaultBackgrounds[boh_type]];
  const qr = properties.includes('qr');
  const qr_card_layout = (_background = background) === null || _background === void 0 ? void 0 : (_background$metadata = _background.metadata) === null || _background$metadata === void 0 ? void 0 : _background$metadata.qr_card_layout;
  const qr_card = !!qr_card_layout;
  const positionStyles = qr_card_layout ? {
    top: qr_card_layout.position.y,
    left: qr_card_layout.position.x,
    height: qr_card_layout.position.height,
    width: qr_card_layout.position.width
  } : {};
  const hasUpcode = TrackingCode && (qr || qr_card);
  const hasSideUpcode = hasUpcode && qr && !qr_card;
  return /*#__PURE__*/React.createElement("div", {
    className: `SpotlightPlayer ${vertical ? 'vertical' : ''} ${rotated ? 'rotated' : ''} ${hasSideUpcode ? 'has-side-upcode' : ''} ${onBackgroundClick ? 'onpointer' : ''}`,
    style: {
      ...style
    }
  }, !isDocument && /*#__PURE__*/React.createElement(Background, {
    volume: volume,
    hasSideUpcode: hasSideUpcode,
    muted: muted,
    background: background,
    playing: playing,
    onProgress: onProgress,
    onReady: () => setBackgroundReady(true),
    metadata: metadata,
    onPlay: onPlay,
    onPause: onPause,
    onEnded: onEnded,
    onError: onError,
    onDuration: onDuration,
    onBackgroundClick: onBackgroundClick,
    loop: loop,
    useCors: useCors,
    reactPlayerRef: reactPlayerRef,
    reactPlayerConfig: reactPlayerConfig
  }), isDocument && /*#__PURE__*/React.createElement(DocumentPaged, {
    metadata: metadata,
    playing: playing,
    onReady: () => setDocumentPagesReady(true),
    onEnded: onEnded
  }), text && !needsBOHTemplate && /*#__PURE__*/React.createElement(TextOverlay, {
    text: text
  }), !needsBOHTemplate && metadata.show_footer_logo && /*#__PURE__*/React.createElement(Foreground, {
    accent_color: accent_color,
    accent_text_color: text === null || text === void 0 ? void 0 : text.accent_color,
    logo: logo,
    metadata: metadata
  }), hasUpcode && /*#__PURE__*/React.createElement(UPCode, {
    origin: origin,
    properties: properties,
    tracking: {
      ...TrackingCode,
      logo
    },
    rightPanelBackgroundColor: accent_color,
    playing: playing,
    positionStyles: positionStyles,
    clickable: clickable,
    organizationId: organizationId,
    deviceId: deviceId,
    isQRCard: qr_card,
    showShortURL: showShortURL
  }), needsBOHTemplate && /*#__PURE__*/React.createElement(BOHComponent, {
    properties: properties,
    type: boh_type,
    data: boh_data,
    metadata: metadata,
    tracking: TrackingCode,
    text: text,
    organizationId: organizationId,
    deviceId: deviceId,
    duration: duration,
    playing: playing,
    brandkit: brandkit
  }), customLayoutType && /*#__PURE__*/React.createElement(CustomLayoutComponent, {
    type: customLayoutType,
    data: customLayoutData
  }));
};
export default SpotlightPlayer;